'use client'

import { useCallback, useState } from 'react'

import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import listingSearchQuery from '@/queries/listingSearchQuery'

import useToggleWatchlisting from './useToggleWatchlisting'
import { WatchlistOutlineButton, WatchlistOutlineIcon } from './WatchlistCTA'
import { WatchlistRemoveConfirmationModal } from './WatchlistRemoveConfirmationModal'

type Props = {
  dateExpires: string
  listingId: string
  listingTitle: string
  storefrontName?: string
  // undefined = no active bid; number > 0 = active bid amount to surface in
  // the confirmation modal.
  bidAmount?: number
  currency?: string
  // Only drives the flag-off confirmation modal's note warning (today's copy).
  note?: string
  listingEndTime?: string
}

export const WatchlistBidCardRemoveButton = ({
  dateExpires,
  listingId,
  listingTitle,
  storefrontName,
  bidAmount,
  currency,
  note,
  listingEndTime,
}: Props) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { dispatch } = useListingSearch()
  const { trackButtonClicked } = useAnalytics()
  const [isModalOpen, setIsModalOpen] = useState(false)
  // On: Remove is immediate, with the Undo toast and no confirmation (MRM-362).
  const isImmediateRemoval = useFlag(FeatureFlags.NOTES_OUTLIVE_WATCHLIST)

  // ADD_ID/REMOVE_ID compute the new allowlist from the reducer's own
  // current state, not a value read by this component ahead of time — this
  // card unmounts once removed (it drops out of the search results this
  // listing filters on), so by the time the removal toast's Undo is
  // actually clicked, a component-local snapshot (even a ref kept in sync
  // via useEffect) can be one render stale: if the next id change resolves
  // synchronously from the search cache, this card unmounts in that same
  // commit, before its own effect ever gets to run for it.
  const handleUndo = useCallback(() => {
    dispatch({ type: 'ADD_ID', id: listingId })
    void queryClient.invalidateQueries({ queryKey: [listingSearchQuery.name] })
  }, [dispatch, listingId, queryClient])

  const { isPending, toggleWatchlistListing } = useToggleWatchlisting({
    listingId,
    dateExpires,
    isWatchlisted: true,
    listingEndTime,
    storefrontName,
    onUndo: handleUndo,
  })

  const handleConfirm = useCallback(async () => {
    const succeeded = await toggleWatchlistListing()
    // On failure the hook already surfaces an error toast — leave the modal
    // open and the card in the grid so the user's action state matches server
    // reality. Only close the modal + optimistically hide the card on success.
    if (!succeeded) return

    trackButtonClicked({
      buttonName: 'remove_from_watchlist_confirmed',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listingId,
      storefront_name: storefrontName,
    })

    setIsModalOpen(false)
    // Drop this listing from the search allowlist so the card disappears
    // immediately from the visible list. The allowlist is the watchlist
    // scoping seeded by PrefetchedWatchlistedListingsProvider;
    // useToggleWatchlisting invalidates allWatchedListingsQuery over the
    // network but does not update this in-flight allowlist, so REMOVE_ID
    // splices it out explicitly here (computed from the reducer's current
    // state, not a snapshot read here — see handleUndo's comment).
    dispatch({ type: 'REMOVE_ID', id: listingId })
    // Invalidate the search cache so any stale entries elsewhere in the app
    // pick up the change on next mount.
    void queryClient.invalidateQueries({ queryKey: [listingSearchQuery.name] })
  }, [
    toggleWatchlistListing,
    dispatch,
    listingId,
    queryClient,
    trackButtonClicked,
    storefrontName,
  ])

  const handleClick = useCallback(() => {
    trackButtonClicked({
      buttonName: 'toggle_listing_watched',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      is_selected: false,
      listing_id: listingId,
      storefront_name: storefrontName,
    })
    if (isImmediateRemoval) {
      void handleConfirm()
      return
    }
    setIsModalOpen(true)
  }, [
    trackButtonClicked,
    listingId,
    storefrontName,
    isImmediateRemoval,
    handleConfirm,
  ])
  // Once confirmed, the removal (the note read, then the DELETE) cannot be
  // called back, so a dismiss while it is pending is ignored rather than
  // closing a modal over a removal that still happens.
  const handleCancel = useCallback(() => {
    if (!isPending) setIsModalOpen(false)
  }, [isPending])

  return (
    <>
      <WatchlistOutlineButton
        type="button"
        onClick={handleClick}
        disabled={isPending}
        aria-label={formatMessage(
          { id: 'Listing.watchlist.removeAriaLabel' },
          { listingTitle }
        )}
        data-testid="watchlist-remove-button"
      >
        <WatchlistOutlineIcon icon={faHeartSolid} />
        {formatMessage({ id: 'Listing.watchlist.removeButtonLabel' })}
      </WatchlistOutlineButton>
      <WatchlistRemoveConfirmationModal
        isOpen={!isImmediateRemoval && isModalOpen}
        listingTitle={listingTitle}
        storefrontName={storefrontName}
        bidAmount={bidAmount}
        currency={currency}
        hasNote={!!note}
        isPending={isPending}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  )
}
